import { HistoryOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { cn } from "../../../utils/cn.ts"
import { selectActiveFilters, setFilters } from "../redux/global-map.slice"

/**
 * "History" — per explicit product direction, this is NOT a filter-bar
 * pill (an earlier version was; see `GlobalMapFilterBar.tsx`'s own git
 * history) but a standalone toggle beside `LayerToggleButtons`' Lead/Project
 * pills, styled to match them exactly per a later refinement: the same
 * `bg-sky-100` rounded-pill wrapper and the identical per-button "pressed =
 * white + shadow, unpressed = dimmed" treatment `LayerToggleButtons` itself
 * uses — a same-shape sibling rectangle, not a plain icon-only circle (the
 * first version of this component). `HistoryOutlined` (a clock face with a
 * counter-clockwise arrow) is Ant Design's own built-in icon for exactly
 * this concept — imported directly from `@ant-design/icons` rather than
 * through this app's custom `Icon` wrapper, which has no registered
 * clock/history glyph; several other call sites in this codebase already
 * import individual `@ant-design/icons` icons the same direct way.
 *
 * Still writes the exact same `GlobalMapFilters.includeHistory` field the
 * backend/URL-sync/Clear-All machinery already understands — only the
 * visual home and shape changed, not the underlying state (see
 * `active-filter-summary.ts`'s own `NON_CHIP_KEYS` comment for why it still
 * lives inside `GlobalMapFilters` rather than becoming a third top-level
 * field like `activeLayers`).
 *
 * The tooltip explains what "Include History" adds — every past project and
 * lead — whichever state the button is in; `aria-pressed` carries the state.
 */
export function HistoryToggleButton() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const enabled = Boolean(filters.includeHistory)

  const toggle = () => {
    dispatch(setFilters({ includeHistory: !enabled }))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "Spacebar"
    ) {
      event.preventDefault()
      toggle()
    }
  }

  const tooltip = t("globalMap.filters.enableHistoryTooltip")
  const label = t("globalMap.filters.history")

  return (
    <div role="group" aria-label={label} className="flex shrink-0 items-center">
      <Tooltip title={tooltip}>
        <button
          type="button"
          aria-pressed={enabled}
          onClick={toggle}
          onKeyDown={handleKeyDown}
          data-testid="global-map-history-toggle"
          className={cn(
            "flex h-10 cursor-pointer items-center gap-2 rounded-lg border px-3.5 text-[13px] font-medium shadow-[0_2px_6px_rgba(15,23,42,0.06)] transition-colors",
            enabled
              ? // History is not an entity, so "on" tints toward the app's own
                // teal rather than borrowing Lead blue or Project green.
                "border-primary/30 bg-primary-bg text-primary-strong"
              : // Off is a plain, fully legible white chip — the dimmed
                // treatment this used to carry read as disabled, which it
                // never is.
                "border-[#E2E8F0] bg-sky-50/70 text-slate-700 hover:border-slate-300 hover:text-slate-900"
          )}
        >
          <HistoryOutlined className="!text-[15px] text-slate-500" />
          {label}
        </button>
      </Tooltip>
    </div>
  )
}

export default HistoryToggleButton
